import React from 'react';
import {
  Bot,
  Clock,
  Database,
  FileText,
  Folder,
  MessagesSquare,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Text } from '@cotera/client/app/components/ui';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  resourceColors,
  type ResourceColorKey,
} from '../resources/type/resource-type-colors';
import { CHART_SERIES_COLORS } from '@cotera/client/v0/components/charts/chart-palette';

const RESOURCE_SWATCHES: {
  name: string;
  color: ResourceColorKey;
  icon: LucideIcon;
}[] = [
  { name: 'Chat', color: 'chat', icon: MessagesSquare },
  { name: 'Agent', color: 'agent', icon: Bot },
  { name: 'Document', color: 'document', icon: FileText },
  { name: 'Trigger', color: 'trigger', icon: Clock },
  { name: 'Workflow', color: 'workflow', icon: Workflow },
  { name: 'Dataset', color: 'dataset', icon: Database },
  { name: 'Folder', color: 'folder', icon: Folder },
  { name: 'Tool', color: 'tool', icon: Wrench },
];

const CHART_COLORS = [
  { n: 1, name: 'Terracotta' },
  { n: 2, name: 'Peach' },
  { n: 3, name: 'Amber' },
  { n: 4, name: 'Wheat' },
  { n: 5, name: 'Rose' },
  { n: 6, name: 'Clay' },
  { n: 7, name: 'Apricot' },
  { n: 8, name: 'Dusty rose' },
  { n: 9, name: 'Sand' },
  { n: 10, name: 'Umber' },
] as const;

// Names + preview bar heights for the chart *series* palette (CHART_SERIES_COLORS),
// in the same slot order as those colors.
const CHART_SERIES_NAMES = [
  'Denim (blue)',
  'Sage (green)',
  'Mauve (violet)',
  'Ochre (amber)',
  'Spruce (steel blue)',
  'Rose (red)',
  'Pine (teal)',
  'Brown (rust)',
] as const;
const CHART_SERIES_BAR_HEIGHTS = [72, 88, 54, 96, 63, 80, 45, 68] as const;

// Static class maps so Tailwind JIT picks them up.
const DOT: Record<number, string> = {
  1: 'bg-chart-1',
  2: 'bg-chart-2',
  3: 'bg-chart-3',
  4: 'bg-chart-4',
  5: 'bg-chart-5',
  6: 'bg-chart-6',
  7: 'bg-chart-7',
  8: 'bg-chart-8',
  9: 'bg-chart-9',
  10: 'bg-chart-10',
};
const BG: Record<number, string> = {
  1: 'bg-chart-1-bg',
  2: 'bg-chart-2-bg',
  3: 'bg-chart-3-bg',
  4: 'bg-chart-4-bg',
  5: 'bg-chart-5-bg',
  6: 'bg-chart-6-bg',
  7: 'bg-chart-7-bg',
  8: 'bg-chart-8-bg',
  9: 'bg-chart-9-bg',
  10: 'bg-chart-10-bg',
};
const FG: Record<number, string> = {
  1: 'text-chart-1-fg',
  2: 'text-chart-2-fg',
  3: 'text-chart-3-fg',
  4: 'text-chart-4-fg',
  5: 'text-chart-5-fg',
  6: 'text-chart-6-fg',
  7: 'text-chart-7-fg',
  8: 'text-chart-8-fg',
  9: 'text-chart-9-fg',
  10: 'text-chart-10-fg',
};
const PILL: Record<number, string> = {
  1: 'bg-chart-1-bg text-chart-1-fg border-chart-1/25',
  2: 'bg-chart-2-bg text-chart-2-fg border-chart-2/25',
  3: 'bg-chart-3-bg text-chart-3-fg border-chart-3/25',
  4: 'bg-chart-4-bg text-chart-4-fg border-chart-4/25',
  5: 'bg-chart-5-bg text-chart-5-fg border-chart-5/25',
  6: 'bg-chart-6-bg text-chart-6-fg border-chart-6/25',
  7: 'bg-chart-7-bg text-chart-7-fg border-chart-7/25',
  8: 'bg-chart-8-bg text-chart-8-fg border-chart-8/25',
  9: 'bg-chart-9-bg text-chart-9-fg border-chart-9/25',
  10: 'bg-chart-10-bg text-chart-10-fg border-chart-10/25',
};

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

export const DesignSystemPage: React.FC = () => {
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-8 py-12 flex flex-col gap-12">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-serif-display">
            Design System
          </h1>
          <p className="text-muted-foreground mt-1">
            V1 theme tokens, components, and extended palette
          </p>
        </div>

        {/* Core colors */}
        <Section title="Core Colors">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[
              { name: 'Background', cls: 'bg-background border border-border' },
              { name: 'Card', cls: 'bg-card border border-border' },
              { name: 'Primary', cls: 'bg-primary' },
              { name: 'Secondary', cls: 'bg-secondary' },
              { name: 'Muted', cls: 'bg-muted' },
              { name: 'Accent', cls: 'bg-accent border border-border' },
              { name: 'Destructive', cls: 'bg-destructive' },
              { name: 'Sidebar', cls: 'bg-sidebar border border-border' },
            ].map((c) => (
              <div key={c.name} className="flex flex-col gap-1.5">
                <div className={cn('h-16 rounded-lg', c.cls)} />
                <span className="text-xs text-muted-foreground">{c.name}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Text colors */}
        <Section title="Text Colors">
          <div className="flex flex-col gap-2 bg-card rounded-lg p-4 border border-border">
            <span className="text-foreground text-sm">
              Foreground — primary text
            </span>
            <span className="text-muted-foreground text-sm">
              Muted foreground — secondary text
            </span>
            <span className="text-primary text-sm">Primary — links, CTA</span>
            <span className="text-destructive text-sm">
              Destructive — errors
            </span>
          </div>
        </Section>

        {/* Extended palette */}
        <Section title="Extended Palette — 16 Chart Colors">
          <p className="text-sm text-muted-foreground">
            Each color has three stops: main (dot), bg (light wash), fg (dark
            text). Used for pills, calendar, badges, and anywhere categorical
            color is needed. Charts use the dedicated series palette below.
          </p>

          {/* Dot swatches */}
          <div className="grid grid-cols-5 gap-3">
            {CHART_COLORS.map((c) => (
              <div key={c.n} className="flex flex-col items-center gap-1">
                <div className={cn('h-8 w-8 rounded-md', DOT[c.n])} />
                <span className="text-[10px] text-muted-foreground text-center leading-tight">
                  {c.name}
                </span>
              </div>
            ))}
          </div>

          {/* Full swatch rows */}
          <div className="flex flex-col gap-1.5">
            {CHART_COLORS.map((c) => (
              <div key={c.n} className="flex items-center gap-2">
                <span className="w-20 text-[11px] text-muted-foreground shrink-0">
                  {c.n}. {c.name}
                </span>
                <div className={cn('h-6 flex-1 rounded', DOT[c.n])} />
                <div className={cn('h-6 flex-1 rounded', BG[c.n])} />
                <div
                  className={cn(
                    'h-6 flex-1 rounded flex items-center justify-center text-[10px] font-medium',
                    BG[c.n],
                    FG[c.n]
                  )}
                >
                  Sample text
                </div>
                <span
                  className={cn(
                    'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium border',
                    PILL[c.n]
                  )}
                >
                  Pill
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Chart series palette */}
        <Section title="Chart Series Colors">
          <p className="text-sm text-muted-foreground">
            The categorical palette the chart builder assigns to bar/line/pie
            series (via <code>--chart-series-1…8</code>). Drawn from the brand
            resource-type hues and ordered so adjacent series stay distinct —
            including for red/green colorblind viewers. Kept separate from the
            shared tokens above so recoloring charts never touches pills,
            calendars, buttons, or toasts.
          </p>

          {/* Mini bar-chart preview */}
          <div className="flex h-40 items-end gap-2 rounded-lg border border-border bg-card p-4">
            {CHART_SERIES_COLORS.map((color, i) => (
              <div
                key={i}
                className="flex-1 rounded-t"
                style={{
                  backgroundColor: color,
                  height: `${CHART_SERIES_BAR_HEIGHTS[i]}%`,
                }}
              />
            ))}
          </div>

          {/* Labeled swatch rows */}
          <div className="flex flex-col gap-1.5">
            {CHART_SERIES_COLORS.map((color, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-28 shrink-0 text-[11px] text-muted-foreground">
                  {i + 1}. {CHART_SERIES_NAMES[i]}
                </span>
                <div
                  className="h-6 flex-1 rounded"
                  style={{ backgroundColor: color }}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* Resource type colors */}
        <Section title="Resource Type Colors">
          <p className="text-sm text-muted-foreground">
            Muted, earthy accent per resource type. The color-coded bubble is
            what the library uses for each icon; the same palette drives the
            dot, light bg wash, border, and text color.
          </p>
          <div className="flex flex-col gap-1.5">
            {RESOURCE_SWATCHES.map((r) => (
              <div key={r.name} className="flex items-center gap-3">
                <span className="w-20 text-[11px] text-muted-foreground shrink-0">
                  {r.name}
                </span>
                <span
                  className={cn(
                    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    resourceColors[r.color].bg,
                    resourceColors[r.color].text
                  )}
                >
                  <r.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div
                  className={cn(
                    'h-6 flex-1 rounded',
                    resourceColors[r.color].dot
                  )}
                />
                <div
                  className={cn(
                    'h-6 flex-1 rounded border',
                    resourceColors[r.color].bg,
                    resourceColors[r.color].border
                  )}
                />
                <div
                  className={cn(
                    'h-6 flex-1 rounded flex items-center justify-center text-[10px] font-medium',
                    resourceColors[r.color].bg,
                    resourceColors[r.color].text
                  )}
                >
                  Sample text
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Typography */}
        <Section title="Typography">
          <div className="bg-card rounded-lg p-6 border border-border flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Display Serif (Fraunces)
              </span>
              <h1 className="text-4xl font-bold font-serif-display text-foreground">
                The quick brown fox
              </h1>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Body Serif (Charis SIL)
              </span>
              <p className="text-lg font-serif text-foreground">
                The quick brown fox jumps over the lazy dog. Pack my box with
                five dozen liquor jugs.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Sans-serif (Inter)
              </span>
              <p className="text-base text-foreground">
                The quick brown fox jumps over the lazy dog. Pack my box with
                five dozen liquor jugs.
              </p>
            </div>

            <div className="border-t border-border pt-4 flex flex-col gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Size scale
              </span>
              {[
                { label: 'text-xs', cls: 'text-xs' },
                { label: 'text-sm', cls: 'text-sm' },
                { label: 'text-base', cls: 'text-base' },
                { label: 'text-lg', cls: 'text-lg' },
                { label: 'text-xl', cls: 'text-xl' },
                { label: 'text-2xl', cls: 'text-2xl' },
                { label: 'text-3xl', cls: 'text-3xl' },
                { label: 'text-4xl', cls: 'text-4xl' },
              ].map((s) => (
                <div key={s.label} className="flex items-baseline gap-3">
                  <span className="w-16 text-[10px] text-muted-foreground shrink-0">
                    {s.label}
                  </span>
                  <span className={cn('text-foreground', s.cls)}>
                    Almost before we knew it, we had left the ground.
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Text components */}
        <Section title="Text Components">
          <div className="bg-card rounded-lg p-6 border border-border flex flex-col gap-3">
            <Text.P>Text.P — standard paragraph</Text.P>
            <Text.Caption>Text.Caption — muted caption text</Text.Caption>
            <Text.Span>Text.Span — inline span</Text.Span>
            <Text.Title type="heading">Text.Title heading</Text.Title>
            <Text.Title type="title">Text.Title title</Text.Title>
            <Text.Title type="section">Text.Title section</Text.Title>
            <Text.Label>Text.Label</Text.Label>
          </div>
        </Section>

        {/* Border radius */}
        <Section title="Border Radius">
          <div className="flex flex-wrap gap-4">
            {[
              { label: 'rounded-none', cls: 'rounded-none' },
              { label: 'rounded-sm', cls: 'rounded-sm' },
              { label: 'rounded (4px)', cls: 'rounded' },
              { label: 'rounded-md', cls: 'rounded-md' },
              { label: 'rounded-lg', cls: 'rounded-lg' },
              { label: 'rounded-xl', cls: 'rounded-xl' },
              { label: 'rounded-full', cls: 'rounded-full' },
            ].map((r) => (
              <div key={r.label} className="flex flex-col items-center gap-1.5">
                <div
                  className={cn(
                    'h-16 w-16 bg-primary/15 border border-primary/30',
                    r.cls
                  )}
                />
                <span className="text-[10px] text-muted-foreground">
                  {r.label}
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Badges */}
        <Section title="Badges">
          <div className="flex flex-wrap gap-2">
            {(
              [
                'blue',
                'amber',
                'purple',
                'primary',
                'gray',
                'orange',
                'rose',
                'brown',
                'green',
                'muted',
              ] as const
            ).map((v) => (
              <Badge key={v} variant={v}>
                {v}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {(['default', 'secondary', 'destructive', 'outline'] as const).map(
              (v) => (
                <Badge key={v} variant={v}>
                  {v}
                </Badge>
              )
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge size="sm">Small</Badge>
            <Badge>Default</Badge>
            <Badge size="lg">Large</Badge>
          </div>
        </Section>

        {/* Buttons */}
        <Section title="Buttons">
          <div className="flex flex-wrap gap-2">
            {(
              [
                'default',
                'outline',
                'secondary',
                'ghost',
                'destructive',
                'link',
              ] as const
            ).map((v) => (
              <Button key={v} variant={v}>
                {v}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm">Small</Button>
            <Button>Default</Button>
            <Button size="lg">Large</Button>
          </div>
        </Section>

        {/* Borders & Dividers */}
        <Section title="Borders">
          <div className="flex flex-col gap-3">
            <div className="h-0 border-t border-border" />
            <span className="text-[10px] text-muted-foreground">
              border-border (default)
            </span>
            <div className="h-0 border-t border-input" />
            <span className="text-[10px] text-muted-foreground">
              border-input
            </span>
            <div className="h-0 border-t-2 border-primary" />
            <span className="text-[10px] text-muted-foreground">
              border-primary (2px)
            </span>
          </div>
        </Section>

        {/* Spacing / Shadows */}
        <Section title="Shadows & Elevation">
          <div className="flex flex-wrap gap-6">
            {[
              { label: 'shadow-sm', cls: 'shadow-sm' },
              { label: 'shadow', cls: 'shadow' },
              { label: 'shadow-md', cls: 'shadow-md' },
              { label: 'shadow-lg', cls: 'shadow-lg' },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-1.5">
                <div
                  className={cn(
                    'h-16 w-24 rounded-lg bg-card border border-border',
                    s.cls
                  )}
                />
                <span className="text-[10px] text-muted-foreground">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};
